import { LitElement, html } from 'lit';
import styles from './studio-input.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioInput extends LitElement {
  static styles = styles;

  static properties = {
    height: { type: String, reflect: true },
    width: { type: String, reflect: true },
    state: { type: String, reflect: true },
    styleType: { type: String, reflect: true, attribute: 'style-type' },
    label: { type: String, reflect: true },
    helperText: { type: String, reflect: true, attribute: 'helper-text' },
    placeholder: { type: String, reflect: true },
    iconLeft: { type: String, reflect: true, attribute: 'icon-left' },
    iconRight: { type: String, reflect: true, attribute: 'icon-right' },
    value: { type: String, reflect: true },
    disabled: { type: Boolean, reflect: true },
    type: { type: String, reflect: true },
  };

  static validHeights = ['tiny', 'small', 'medium', 'large', 'giant'];
  static validStates = ['default', 'filled', 'hover', 'focus', 'disabled', 'success', 'info', 'warning', 'error'];
  static validStyleTypes = ['outline', 'filled'];
  static validTypes = ['text', 'password', 'email', 'number', 'tel', 'search', 'url'];

  constructor() {
    super();
    this.height = 'medium';
    this.width = 'stretch';
    this.state = 'default';
    this.styleType = 'outline';
    this.label = '';
    this.helperText = '';
    this.placeholder = '';
    this.iconLeft = '';
    this.iconRight = '';
    this.value = '';
    this.disabled = false;
    this.type = 'text';
    this._eventListeners = [];
  }

  set height(value) {
    const oldValue = this._height;
    this._height = StudioInput.validHeights.includes(value) ? value : 'medium';
    this.requestUpdate('height', oldValue);
  }

  get height() {
    return this._height;
  }

  set width(value) {
    const oldValue = this._width;
    this._width = typeof value === 'string' && value ? value : 'stretch';
    this.requestUpdate('width', oldValue);
  }

  get width() {
    return this._width;
  }

  set state(value) {
    this._state = StudioInput.validStates.includes(value) ? value : 'default';
  }

  get state() {
    return this._state;
  }

  set styleType(value) {
    this._styleType = StudioInput.validStyleTypes.includes(value) ? value : 'outline';
  }

  get styleType() {
    return this._styleType;
  }

  set type(value) {
    this._type = StudioInput.validTypes.includes(value) ? value : 'text';
  }

  get type() {
    return this._type;
  }

  _hasCustomState() {
    return ['success', 'info', 'warning', 'error'].includes(this.state);
  }

  firstUpdated() {
    const input = this.renderRoot.querySelector('input');
    if (!input) return;

    this._eventListeners = [
      {
        event: 'input',
        handler: (e) => {
          if (this.disabled || this.state === 'disabled') return;
          this.value = e.target.value;
          if (!this._hasCustomState()) {
            this.state = this.value ? 'filled' : 'default';
          }
          this.dispatchEvent(new CustomEvent('input', { detail: this.value }));
        },
      },
      {
        event: 'focus',
        handler: () => {
          if (this.disabled || this.state === 'disabled' || this._hasCustomState()) return;
          this.state = 'focus';
        },
      },
      {
        event: 'blur',
        handler: () => {
          if (this.disabled || this.state === 'disabled' || this._hasCustomState()) return;
          this.state = this.value ? 'filled' : 'default';
        },
      },
      {
        event: 'mouseenter',
        handler: () => {
          if (this.disabled || this.state === 'disabled' || this._hasCustomState()) return;
          if (this.state !== 'focus') this.state = 'hover';
        },
      },
      {
        event: 'mouseleave',
        handler: () => {
          if (this.disabled || this.state === 'disabled' || this._hasCustomState()) return;
          if (this.state !== 'focus') this.state = this.value ? 'filled' : 'default';
        },
      },
    ];

    this._eventListeners.forEach(({ event, handler }) =>
      input.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const input = this.renderRoot.querySelector('input');
    if (input && this._eventListeners) {
      this._eventListeners.forEach(({ event, handler }) =>
        input.removeEventListener(event, handler)
      );
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('width')) {
      this.style.setProperty('--input-width', this.width);
      this.style.setProperty('--input-min-width', this.width === 'stretch' ? '300px' : this.width);
    }
  }

  render() {
    return html`
      <div class="input-wrapper">
        <slot name="label">
          ${this.label ? html`<label for="input" class="label">${this.label}</label>` : ''}
        </slot>
        <div part="input-container" class="input-container">
          <div class="icon left">
            <slot name="left">
              ${this.iconLeft ? html`<studio-icon name="${this.iconLeft}" size="24px"></studio-icon>` : ''}
            </slot>
          </div>
          <input
            id="input"
            type="${this.type}"
            .value="${this.value}"
            placeholder="${this.placeholder}"
            ?disabled="${this.disabled}"
            aria-label="${this.getAttribute('aria-label') || this.label || 'Input'}"
            aria-disabled="${this.disabled}"
          />
          <div class="icon right">
            <slot name="right">
              ${this.iconRight ? html`<studio-icon name="${this.iconRight}" size="24px"></studio-icon>` : ''}
            </slot>
          </div>
        </div>
        <slot name="helper-text">
          ${this.helperText ? html`<span class="helper-text">${this.helperText}</span>` : ''}
        </slot>
        <slot></slot>
      </div>
    `;
  }
}

customElements.define('studio-input', StudioInput);